import type {
  CashflowData,
  CashflowMonth,
  Expense,
  ExpenseKind,
  UpcomingData,
} from "@/services/edgeFunctionWrappers/accountingManagementWrapper";

/**
 * Logique d'affichage du module comptabilité, pure et testée — les
 * composants ne font que rendre ce que ces fonctions calculent
 * (même convention que lib/payments/paymentsView).
 */

export interface MonthlyPoint extends CashflowMonth {
  /** Solde net cumulé depuis le début de la série. */
  cumul: number;
  /** CA « engagé » du mois (subscriptions.price par mois de démarrage). */
  invoiced: number;
}

/**
 * Série mensuelle prête pour le graphique : fusionne monthly + invoiced,
 * zéro-fill les mois absents (l'edge les fournit déjà zéro-fillés, mais on
 * ne dépend pas de cette garantie), trie par mois et cumule le net.
 */
export function buildMonthlySeries(data: Pick<CashflowData, "monthly" | "invoiced">): MonthlyPoint[] {
  const byMonth = new Map<string, MonthlyPoint>();
  for (const m of data.monthly ?? []) {
    byMonth.set(m.month, { ...m, cumul: 0, invoiced: 0 });
  }
  for (const inv of data.invoiced ?? []) {
    const existing = byMonth.get(inv.month);
    if (existing) existing.invoiced = inv.amount;
    else byMonth.set(inv.month, { month: inv.month, cash_in: 0, cash_out: 0, net: 0, cumul: 0, invoiced: inv.amount });
  }
  const sorted = [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
  let running = 0;
  for (const point of sorted) {
    running += point.net;
    point.cumul = running;
  }
  return sorted;
}

export interface AccountingKpis {
  cashIn: number;
  cashOut: number;
  pendingIn: number;
  dueOut: number;
  /** Marge de la période : encaissé - décaissé. */
  margin: number;
}

export function deriveKpis(data: CashflowData | null): AccountingKpis {
  if (!data) {
    return { cashIn: 0, cashOut: 0, pendingIn: 0, dueOut: 0, margin: 0 };
  }
  const cashIn = data.summary.cash_in ?? 0;
  const cashOut = data.summary.cash_out ?? 0;
  return {
    cashIn,
    cashOut,
    pendingIn: data.summary.pending_in,
    dueOut: data.summary.due_out,
    margin: cashIn - cashOut,
  };
}

/** Ventilation par catégorie triée décroissante, avec pourcentages. */
export function categoryBreakdown(byCategory: Record<string, number>) {
  const entries = Object.entries(byCategory ?? {}).filter(([, v]) => v > 0);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return entries
    .map(([category, amount]) => ({
      category,
      amount,
      pct: total > 0 ? Math.round((amount / total) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

export interface ExpenseFilters {
  kind?: ExpenseKind | "all";
  status?: "due" | "paid" | "cancelled" | "all";
  category?: string | "all";
  search?: string;
}

/** Filtre client du journal (le gros du filtrage est déjà fait par l'edge). */
export function filterExpensesView(rows: Expense[], filters: ExpenseFilters): Expense[] {
  let out = rows;
  if (filters.kind && filters.kind !== "all") out = out.filter((r) => r.kind === filters.kind);
  if (filters.status && filters.status !== "all") out = out.filter((r) => r.status === filters.status);
  if (filters.category && filters.category !== "all") out = out.filter((r) => r.category === filters.category);
  const q = (filters.search ?? "").trim().toLowerCase();
  if (q) {
    const tokens = q.split(/\s+/).filter(Boolean);
    out = out.filter((r) => {
      const hay = [r.label, r.invoice_number, r.employee_name, r.notes, r.suppliers?.name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return tokens.every((tok) => hay.includes(tok));
    });
  }
  return out;
}

export type ExpenseGroupBy = "kind" | "category" | "status" | "none";

export interface ExpenseGroup {
  /** Clé de regroupement ("all" quand aucun groupement). */
  key: string;
  rows: Expense[];
  total: number;
}

/**
 * Regroupe le journal par type / catégorie / statut avec sous-totaux,
 * trié par montant décroissant (groupes les plus lourds en premier).
 */
export function groupExpensesView(rows: Expense[], by: ExpenseGroupBy): ExpenseGroup[] {
  if (by === "none") {
    return rows.length === 0
      ? []
      : [{ key: "all", rows, total: rows.reduce((s, r) => s + (Number(r.amount) || 0), 0) }];
  }
  const map = new Map<string, ExpenseGroup>();
  for (const row of rows) {
    const key = String(row[by] ?? "other");
    const group = map.get(key) ?? { key, rows: [], total: 0 };
    group.rows.push(row);
    group.total += Number(row.amount) || 0;
    map.set(key, group);
  }
  return [...map.values()].sort((a, b) => b.total - a.total || a.key.localeCompare(b.key));
}

export interface UpcomingItem {
  id: string;
  date: string;
  label: string;
  amount: number;
  direction: "in" | "out";
  /** Solde projeté cumulé après cet élément. */
  projected: number;
}

/**
 * Timeline fusionnée des échéances à venir : sorties (dépenses dues) en
 * négatif, entrées (échéancier client) en positif, triée par date, avec
 * solde projeté cumulé à partir de `startingBalance`.
 */
export function mergeUpcoming(data: UpcomingData | null, startingBalance = 0): UpcomingItem[] {
  if (!data) return [];
  const items: Omit<UpcomingItem, "projected">[] = [
    ...(data.expenses_due ?? []).map((e) => ({
      id: `out-${e.id}`,
      date: (e.due_date ?? e.expense_date) as string,
      label: e.suppliers?.name ? `${e.label} — ${e.suppliers.name}` : e.label,
      amount: Number(e.amount) || 0,
      direction: "out" as const,
    })),
    ...(data.schedules_due ?? []).map((s) => ({
      id: `in-${s.id}`,
      date: s.due_date,
      label: `#${s.installment_number}`,
      amount: Number(s.amount) || 0,
      direction: "in" as const,
    })),
  ];
  items.sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
  let projected = startingBalance;
  return items.map((item) => {
    projected += item.direction === "in" ? item.amount : -item.amount;
    return { ...item, projected };
  });
}
